/* eslint-disable array-callback-return */
import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import {
  Container,
  Segment,
  Header,
  Icon,
  Grid,
  Label,
  Message,
  Button,
  Table,
  Input,
  Modal,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import moment from "moment";
import { PDFViewer } from "@react-pdf/renderer";

import { db } from "../firebase/firebaseConfig";
import { getDocs, collection, where, query } from "firebase/firestore";
import { UserContext } from "../context/UserContext";
import ReportePdf from "./ReporteMantePrevPDF";

const COLORS = {
  teal: "#00b5ad",
  tealSoft: "#e8f8f7",
  blue: "#2185d0",
  blueSoft: "#edf6fc",
  green: "#21ba45",
  greenSoft: "#edf9f0",
  border: "#e5e7eb",
  text: "#1f2937",
  muted: "#6b7280",
  background: "#f8fafc",
};

const cardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0,0,0,.06)",
};

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#374151",
  whiteSpace: "nowrap",
};

const safeText = (v) => {
  if (v === null || v === undefined) return "";
  if (v?.toDate) return moment(v.toDate()).format("DD/MM/YYYY HH:mm");

  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  return String(v);
};

const ReporteMantoPrevPRO = () => {
  const { users } = useContext(UserContext);

  // base list
  const [ingresos, setIngresos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [buscador, setBuscador] = useState("");

  // PDF
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cabecera, setCabecera] = useState([]);
  const [itemsInst, setItemsInst] = useState([]);
  const [itemsCheck, setItemsCheck] = useState([]);
  const [itemsMed, setItemsMed] = useState([]);
  const [itemsSeg, setItemsSeg] = useState([]);
  const [obs, setObs] = useState("");
  const [userAdd, setUserAdd] = useState([]);

  const formatearFecha = (f) =>
    f?.toDate ? moment(f.toDate()).format("DD/MM/YYYY HH:mm") : "";

  // -------------------- Load list --------------------
  const loadIngresos = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "ingresostcab"),
      where("emp_id", "==", users.emp_id),
      where("estado", "in", ["REALIZADO", "EN RETIRO", "ENTREGADO"])
    );

    const snap = await getDocs(q);
    setIngresos(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  }, [users?.emp_id]);

  const loadUsuarios = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "usuarios"),
      where("emp_id", "==", users.emp_id)
    );

    const snap = await getDocs(q);
    setUsuarios(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  }, [users?.emp_id]);

  useEffect(() => {
    const init = async () => {
      if (!users?.emp_id) return;

      setLoading(true);

      try {
        await Promise.all([loadIngresos(), loadUsuarios()]);
      } catch (error) {
        console.error("Error cargando mantenciones:", error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [users?.emp_id, loadIngresos, loadUsuarios]);

  // -------------------- Mantenciones preventivas --------------------
  const dataMP = useMemo(() => {
    return [...ingresos]
      .filter((x) => x.servicio === "MANTENCION PREVENTIVA")
      .sort((a, b) => (b.fecha_out ?? 0) - (a.fecha_out ?? 0));
  }, [ingresos]);

  // -------------------- Buscador --------------------
  const filteredData = useMemo(() => {
    const q = (buscador || "").trim().toLowerCase();

    if (!q) return dataMP;

    return dataMP.filter((item) =>
      Object.keys(item).some((k) =>
        safeText(item[k]).toLowerCase().includes(q)
      )
    );
  }, [dataMP, buscador]);

  // -------------------- KPIs --------------------
  const kpis = useMemo(() => {
    const total = dataMP.length;
    const conEstado = dataMP.filter((x) => Boolean(x.estado_servicio)).length;

    return { total, conEstado };
  }, [dataMP]);

  // -------------------- PDF --------------------
  const pdfver = useCallback(
    async (idIngreso) => {
      if (!users?.emp_id) return;

      setPdfLoading(true);
      setOpenPdf(true);

      try {
        const cab = dataMP.filter((x) => x.id === idIngreso);
        setCabecera(cab);

        const qCab = query(
          collection(db, "bitacoracabst"),
          where("emp_id", "==", users.emp_id),
          where("id_ingreso", "==", idIngreso)
        );

        const cabSnap = await getDocs(qCab);
        const cabArr = cabSnap.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));

        const cabId = cabArr?.[0]?.id || "";
        setObs(cabArr?.[0]?.observaciones || "");

        const tecnico = cab?.[0]?.tecnico;
        setUserAdd(usuarios.filter((u) => u.correo === tecnico));

        if (!cabId) {
          setItemsInst([]);
          setItemsCheck([]);
          setItemsMed([]);
          setItemsSeg([]);
          return;
        }

        const base = (categoria) =>
          query(
            collection(db, "bitacorasst"),
            where("emp_id", "==", users.emp_id),
            where("cab_id_bitacora", "==", cabId),
            where("categoria", "==", categoria)
          );

        const [snapInst, snapCheck, snapMed, snapSeg] = await Promise.all([
          getDocs(base("INSTRUMENTOS")),
          getDocs(base("CHECK")),
          getDocs(base("MEDICION")),
          getDocs(base("SEGURIDAD")),
        ]);

        const ordenarPorFecha = (arr) =>
          [...arr].sort(
            (a, b) => (a?.fechaadd ?? 0) - (b?.fechaadd ?? 0)
          );

        setItemsInst(
          ordenarPorFecha(
            snapInst.docs.map((d) => ({ ...d.data(), id: d.id }))
          )
        );

        setItemsCheck(
          ordenarPorFecha(
            snapCheck.docs.map((d, idx) => ({
              ...d.data(),
              id: d.id,
              id2: idx + 1,
            }))
          )
        );

        setItemsMed(
          ordenarPorFecha(
            snapMed.docs.map((d, idx) => ({
              ...d.data(),
              id: d.id,
              id2: idx + 1,
            }))
          )
        );

        setItemsSeg(
          ordenarPorFecha(
            snapSeg.docs.map((d, idx) => ({
              ...d.data(),
              id: d.id,
              id2: idx + 1,
            }))
          )
        );
      } catch (e) {
        console.error("Error al obtener los datos:", e);
      } finally {
        setPdfLoading(false);
      }
    },
    [users?.emp_id, dataMP, usuarios]
  );

  return (
    <Container
      fluid
      style={{
        background: COLORS.background,
        minHeight: "100vh",
        padding: "18px 16px 35px",
      }}
    >
      <Container fluid style={{ maxWidth: 1400 }}>

        {/* CABECERA */}
        <Segment style={{ ...cardStyle, padding: "18px 20px", marginBottom: 16 }}>
          <Grid stackable verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={16}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <Label
                    size="massive"
                    style={{
                      background: COLORS.tealSoft,
                      color: COLORS.teal,
                      margin: 0,
                    }}
                  >
                    <Icon name="clipboard check" style={{ margin: 0 }} />
                  </Label>

                  <Header
                    as="h2"
                    style={{ margin: 0, color: COLORS.text, fontSize: 21 }}
                  >
                    Mantención Preventiva
                    <Header.Subheader style={{ marginTop: 5 }}>
                      Consulta de mantenciones realizadas y certificados técnicos
                    </Header.Subheader>
                  </Header>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* KPIs */}
        <Grid stackable columns={2} style={{ marginBottom: 8 }}>
          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{
                    background: COLORS.tealSoft,
                    color: COLORS.teal,
                  }}
                >
                  <Icon name="clipboard outline" />
                </Label>

                <div>
                  <div style={{ color: COLORS.muted, fontSize: 12 }}>
                    Mantenciones registradas
                  </div>

                  <strong style={{ fontSize: 23, color: COLORS.text }}>
                    {kpis.total}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{
                    background: COLORS.greenSoft,
                    color: COLORS.green,
                  }}
                >
                  <Icon name="check circle outline" />
                </Label>

                <div>
                  <div style={{ color: COLORS.muted, fontSize: 12 }}>
                    Con estado de servicio
                  </div>

                  <strong style={{ fontSize: 23, color: COLORS.text }}>
                    {kpis.conEstado}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* BUSCADOR */}
        <Segment style={{ ...cardStyle, marginTop: 12 }}>
          <Grid stackable verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={13}>
                <Header as="h4" style={{ marginBottom: 10 }}>
                  <Icon name="filter" color="teal" />
                  <Header.Content>
                    Búsqueda de mantenciones
                    <Header.Subheader>
                      Folio, RUT, cliente, equipo, serie, fechas u otro dato
                    </Header.Subheader>
                  </Header.Content>
                </Header>

                <Input
                  fluid
                  focus
                  placeholder="Escriba para buscar..."
                  value={buscador}
                  onChange={(e) => setBuscador(e.target.value)}
                  icon={{
                    name: buscador.trim() ? "close" : "search",
                    link: true,
                    onClick: () =>
                      buscador.trim() ? setBuscador("") : null,
                  }}
                />
              </Grid.Column>

              <Grid.Column width={3} textAlign="right">
                <Button
                  basic
                  fluid
                  icon
                  labelPosition="left"
                  onClick={() => setBuscador("")}
                  disabled={!buscador}
                >
                  <Icon name="eraser" />
                  Limpiar
                </Button>
              </Grid.Column>
            </Grid.Row>

            {!!buscador.trim() && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message info size="small" style={{ margin: 0 }}>
                    <Icon name="info circle" />
                    Mostrando <b>{filteredData.length}</b> resultado(s) para{" "}
                    <b>{buscador}</b>.
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* RESULTADOS */}
        <Segment
          raised
          style={{
            ...cardStyle,
            padding: 0,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "13px 16px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Header as="h4" style={{ margin: 0 }}>
              <Icon name="list alternate outline" color="teal" />
              <Header.Content>
                Mantenciones realizadas
                <Header.Subheader>
                  Certificados de mantención preventiva
                </Header.Subheader>
              </Header.Content>
            </Header>

            <Label basic color="teal">
              {filteredData.length} registro(s)
            </Label>
          </div>

          {loading ? (
            <div style={{ height: 220, position: "relative" }}>
              <Dimmer active inverted>
                <Loader size="large">Procesando...</Loader>
              </Dimmer>
            </div>
          ) : filteredData.length === 0 ? (
            <Message
              warning
              icon
              style={{ margin: 16 }}
            >
              <Icon name="warning circle" />
              <Message.Content>
                <Message.Header>Sin registros</Message.Header>
                {buscador.trim()
                  ? "No se encontraron coincidencias para la búsqueda."
                  : "No existen mantenciones preventivas realizadas."}
              </Message.Content>
            </Message>
          ) : (
            <div
              style={{
                overflowX: "auto",
                maxWidth: "100%",
                overflowY: "auto",
                maxHeight: 520,
              }}
            >
              <Table
                celled
                striped
                selectable
                compact="very"
                singleLine
                style={{ fontSize: 11, margin: 0 }}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={stickyTh}>#</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Orden</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>RUT</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Cliente</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Cierre</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Entrega</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Equipo</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Serie</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh} textAlign="center">
                      Reporte
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {filteredData.map((item, index) => (
                    <Table.Row key={item.id ?? index}>
                      <Table.Cell>{index + 1}</Table.Cell>

                      <Table.Cell style={{ fontWeight: 600 }}>
                        {item.folio}
                      </Table.Cell>

                      <Table.Cell>{item.rut || "-"}</Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace: "normal",
                          fontWeight: 600,
                        }}
                      >
                        {item.entidad || "-"}
                      </Table.Cell>

                      <Table.Cell style={{ whiteSpace: "nowrap" }}>
                        {formatearFecha(item.fecha_out) || "-"}
                      </Table.Cell>

                      <Table.Cell style={{ whiteSpace: "nowrap" }}>
                        {formatearFecha(item.fechamod) || "-"}
                      </Table.Cell>

                      <Table.Cell style={{ whiteSpace: "normal" }}>
                        {item.tipo || "-"}
                      </Table.Cell>

                      <Table.Cell>{item.serie || "-"}</Table.Cell>

                      <Table.Cell textAlign="center">
                        <Button
                          basic
                          compact
                          icon
                          size="mini"
                          onClick={() => pdfver(item.id)}
                          title="Ver PDF"
                          style={{
                            color: "#db2828",
                            background: "#fff",
                            boxShadow: "none",
                          }}
                        >
                          <Icon name="file pdf outline" color="red" style={{ margin: 0 }} />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </Segment>

        {/* MODAL PDF */}
        <Modal
          open={openPdf}
          onClose={() => setOpenPdf(false)}
          size="large"
          closeIcon
        >
          <Modal.Header>
            <Icon name="file pdf outline" color="red" />
            Reporte Mantención Preventiva

            {cabecera?.[0]?.folio && (
              <Label basic style={{ marginLeft: 10 }}>
                Folio {cabecera[0].folio}
              </Label>
            )}
          </Modal.Header>

          <Modal.Content style={{ padding: 0 }}>
            {pdfLoading ? (
              <div style={{ height: 560 }}>
                <Dimmer active inverted>
                  <Loader size="large">Procesando...</Loader>
                </Dimmer>
              </div>
            ) : (
              <>
                {cabecera?.length > 0 &&
                  itemsCheck.length === 0 &&
                  itemsMed.length === 0 &&
                  itemsSeg.length === 0 && (
                    <Message warning icon style={{ margin: 12 }}>
                      <Icon name="warning circle" />
                      <Message.Content>
                        <Message.Header>
                          Sin bitácora asociada
                        </Message.Header>
                        No se encontraron registros en{" "}
                        <b>bitacorasst</b> para este ingreso.
                      </Message.Content>
                    </Message>
                  )}

                <PDFViewer
                  width="100%"
                  height="560"
                  style={{ border: "1px solid #ddd" }}
                >
                  <ReportePdf
                    cabecera={cabecera}
                    itemsInst={itemsInst}
                    itemsCheck={itemsCheck}
                    itemsMed={itemsMed}
                    itemsSeg={itemsSeg}
                    usuario={userAdd}
                    empresa={users?.emp_id}
                    obs={obs}
                  />
                </PDFViewer>
              </>
            )}
          </Modal.Content>

          <Modal.Actions>
            <Button
              icon
              labelPosition="left"
              onClick={() => setOpenPdf(false)}
              color="teal"
            >
              <Icon name="close" />
              Cerrar
            </Button>
          </Modal.Actions>
        </Modal>

      </Container>
    </Container>
  );
};

export default ReporteMantoPrevPRO;